import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Trash,
  Spinner,
  Globe,
  MagnifyingGlass
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDomains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/domains`, { withCredentials: true });
      setDomains(response.data);
    } catch (err) {
      console.error('Error fetching domains:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setError('');
    const domain = newDomain.trim().toLowerCase();
    if (!domain || !domain.includes('.')) {
      setError('Digite um dominio valido (ex: empresa.com.br)');
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/domains`, { domain }, { withCredentials: true });
      setNewDomain('');
      fetchDomains();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao adicionar dominio');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Remover o dominio "${domain}"? Novos registros deste dominio serao tratados como Cliente Externo.`)) return;
    try {
      await axios.delete(`${API_URL}/api/domains/${domain}`, { withCredentials: true });
      fetchDomains();
    } catch (err) {
      console.error('Error removing domain:', err);
    }
  };

  const filteredDomains = domains.filter(d =>
    d.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-domains">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Dominios Permitidos</h1>
        <p className="text-[#5C6B7A] mb-8">
          Emails com esses dominios serao automaticamente registrados como <span className="font-semibold text-[#2980B9]">Cliente Interno</span>
        </p>

        {/* Add Domain Form */}
        <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-6 mb-6">
          <form onSubmit={handleAddDomain} className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-sm font-semibold text-[#1A252F] mb-2 block">Adicionar Novo Dominio</label>
              <div className="relative">
                <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
                <Input
                  type="text"
                  value={newDomain}
                  onChange={(e) => { setNewDomain(e.target.value); setError(''); }}
                  placeholder="ex: empresa.com.br"
                  className="pl-10 h-11 bg-[#F8F9FA] border-[#BDC3C7] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F]"
                  data-testid="domain-input"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              disabled={adding}
              className="h-11 bg-[#27AE60] hover:bg-[#219653] text-white font-medium px-6"
              data-testid="add-domain-btn"
            >
              {adding ? <Spinner size={18} className="animate-spin" /> : <Plus size={18} className="mr-1" />}
              Adicionar
            </Button>
          </form>
          {error && (
            <p className="mt-3 text-sm text-[#E74C3C]" data-testid="domain-error">{error}</p>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-[#2980B9]/5 border border-[#2980B9]/20 rounded-xl p-4 mb-6">
          <p className="text-sm text-[#2980B9]">
            <span className="font-semibold">Como funciona:</span> Quando um usuario se registrar com um email cujo dominio esta nesta lista, 
            ele sera automaticamente classificado como <strong>Cliente Interno</strong>, com acesso aos cursos de Modo Supervisor e Manutencao. 
            Emails de outros dominios serao <strong>Cliente Externo</strong>.
          </p>
        </div>

        {/* Search */}
        {domains.length > 5 && (
          <div className="mb-4">
            <div className="relative max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
              <Input
                type="text"
                placeholder="Filtrar dominios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-[#BDC3C7]"
                data-testid="domain-search"
              />
            </div>
          </div>
        )}

        {/* Domains Table */}
        <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8F9FA]">
                <TableHead className="font-semibold text-[#1A252F]">Dominio</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Adicionado em</TableHead>
                <TableHead className="font-semibold text-[#1A252F] text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDomains.map((item) => (
                <TableRow key={item.domain} data-testid={`domain-row-${item.domain}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#2980B9]/10 flex items-center justify-center">
                        <Globe size={18} className="text-[#2980B9]" />
                      </div>
                      <span className="font-medium text-[#1A252F]">@{item.domain}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[#5C6B7A]">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleRemoveDomain(item.domain)}
                      className="text-[#E74C3C] hover:text-[#E74C3C] hover:bg-[#E74C3C]/10"
                      data-testid={`remove-domain-${item.domain}`}
                    >
                      <Trash size={18} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredDomains.length === 0 && (
            <div className="py-12 text-center text-[#5C6B7A]" data-testid="no-domains">
              {domains.length === 0 
                ? 'Nenhum dominio cadastrado. Adicione dominios para ativar o registro automatico de Cliente Interno.'
                : 'Nenhum dominio encontrado com esse filtro.'}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminDomains;
